import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import HeaderBar from "components/ui/HeaderBar";
import RoleBasedSidebar from "components/ui/RoleBasedSidebar";
import BreadcrumbNavigation from "components/ui/BreadcrumbNavigation";
import StatusIndicatorBadge from "components/ui/StatusIndicatorBadge";
import Icon from "components/AppIcon";
import PaginationComponent from "components/PaginationComponent";
import { useBiodataCache } from "context/BiodataCacheContext";
import { biodataAPI } from "utils/api";
import { useAuth } from "context/AuthContext";
import { getUserRoleForComponents } from "utils/authHelpers";
import BiodataDetailsView from "components/BiodataDetailsView";
import FilterPanel from "../clab-biodata-pending/components/FilterPanel";

const DEFAULT_FILTERS = {
  search: "",
  nationality: "",
  recruiter_id: "",
  date_from: "",
  date_to: "",
};

const ClabBiodataDrafts = () => {
  const navigate = useNavigate();
  const { user, userProfile } = useAuth();
  const userRole = getUserRoleForComponents(user);
  const { getBiodataByStatus, invalidateOnUpdate } = useBiodataCache();

  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [biodataList, setBiodataList] = useState([]);
  const [biodataPagination, setBiodataPagination] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedBiodataDetails, setSelectedBiodataDetails] = useState(null);

  const isAuthorizedUser = useMemo(() => {
    if (!userProfile) return false;
    const allowedRoles = ["clab_user", "clab", "admin", "master_admin", "1", 1];
    return allowedRoles.includes(userProfile.role) || allowedRoles.includes(userProfile.role?.toLowerCase());
  }, [userProfile]);

  useEffect(() => {
    if (userProfile && !isAuthorizedUser) {
      navigate("/login-screen");
    }
  }, [userProfile, isAuthorizedUser, navigate]);

  useEffect(() => {
    const controller = new AbortController();

    const fetchDraftBiodata = async () => {
      try {
        setLoading(true);
        setError(null);

        const params = {
          page: currentPage,
          per_page: itemsPerPage,
          search: filters.search || undefined,
          nationality: filters.nationality || undefined,
          recruiter_id: filters.recruiter_id || undefined,
          date_from: filters.date_from || undefined,
          date_to: filters.date_to || undefined,
        };

        const result = await getBiodataByStatus("draft", params);

        if (result.fromCache) {
        }

        if (controller.signal.aborted) return;

        if (result.success) {
          const items = Array.isArray(result.records)
            ? result.records
            : Array.isArray(result.data?.data)
              ? result.data.data
              : [];
          setBiodataList(items);
          setBiodataPagination(result.pagination || result.data?.pagination || null);
        } else {
          setError(result.error || result.message || "Failed to load draft biodata");
          setBiodataList([]);
          setBiodataPagination(null);
        }
      } catch (err) {
        if (!controller.signal.aborted) {
          setError("Failed to load draft biodata");
        }
      } finally {
        if (!controller.signal.aborted) {
          setLoading(false);
        }
      }
    };

    fetchDraftBiodata();

    return () => controller.abort();
  }, [currentPage, filters, itemsPerPage]);

  useEffect(() => {
    setCurrentPage(1);
  }, [filters, itemsPerPage]);

  const handleSidebarToggle = () => {
    setSidebarCollapsed((prev) => !prev);
  };

  const handleViewDetails = async (biodata) => {
    try {
      const result = await biodataAPI.getBiodataById(biodata.id);
      if (result?.data) {
        setSelectedBiodataDetails(result.data);
        setShowDetailsModal(true);
      } else {
        setError("Failed to load biodata details");
      }
    } catch {
      setError("Failed to load biodata details");
    }
  };

  if (!userProfile || !isAuthorizedUser) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <Icon name="Loader2" size={48} className="animate-spin text-primary mx-auto mb-4" />
          <p className="text-text-secondary">Verifying access...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <HeaderBar
        userRole={userRole}
        userName={userProfile?.full_name || "CLAB User"}
        onSidebarToggle={handleSidebarToggle}
      />

      <RoleBasedSidebar
        userRole={userRole}
        isCollapsed={sidebarCollapsed}
        onToggle={handleSidebarToggle}
      />

      <main
        className={`transition-all duration-300 pt-16 ${sidebarCollapsed ? "lg:ml-16" : "lg:ml-60"}`}
      >
        <div className="p-6">
          <BreadcrumbNavigation userRole={userRole} />

          <div className="mb-8">
            <h1 className="text-3xl font-bold text-text-primary mb-2">Draft Biodata Overview</h1>
            <p className="text-text-secondary">
              Review recruiter drafts to monitor progress before submission.
            </p>
          </div>

          {error && (
            <div className="bg-error-50 border border-error-200 rounded-lg p-4 mb-6">
              <div className="flex items-center space-x-2">
                <Icon name="AlertCircle" size={20} className="text-error" />
                <h3 className="font-semibold text-error-800">Error</h3>
              </div>
              <p className="text-error-700 mt-2">{error}</p>
              <button
                onClick={() => setError(null)}
                className="mt-3 text-sm text-error-600 hover:text-error-800 font-medium"
              >
                Dismiss
              </button>
            </div>
          )}

          <div className="space-y-6">
            <FilterPanel filters={filters} onFiltersChange={setFilters} />

            <div className="card">
              <div className="p-6 border-b border-secondary-200">
                <div className="flex items-center justify-between">
                  <h2 className="text-xl font-semibold text-text-primary">
                    Draft Biodata ({biodataPagination ? biodataPagination.total : 0})
                  </h2>
                  <button
                    onClick={() => setFilters({ ...filters })}
                    className="flex items-center space-x-2 px-4 py-2 text-secondary-700 bg-surface border border-secondary-300 rounded-md hover:bg-secondary-50 transition-colors duration-150"
                  >
                    <Icon name="RefreshCw" size={16} />
                    <span>Refresh</span>
                  </button>
                </div>
              </div>

              {loading ? (
                <div className="p-12 flex items-center justify-center">
                  <div className="text-center">
                    <Icon name="Loader2" size={32} className="animate-spin text-primary mx-auto mb-4" />
                    <p className="text-text-secondary">Loading draft biodata...</p>
                  </div>
                </div>
              ) : biodataList.length === 0 ? (
                <div className="p-12 text-center">
                  <Icon name="FileText" size={48} className="text-secondary-300 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-text-primary mb-2">No Draft Biodata</h3>
                  <p className="text-text-secondary">
                    Recruiters have not saved any draft biodata matching your filters.
                  </p>
                </div>
              ) : (
                <>
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-secondary-50 border-b border-secondary-200">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-medium text-secondary-700 uppercase tracking-wider">
                            Worker No
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-secondary-700 uppercase tracking-wider">
                            Name
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-secondary-700 uppercase tracking-wider">
                            Passport No
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-secondary-700 uppercase tracking-wider">
                            Nationality
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-secondary-700 uppercase tracking-wider">
                            Recruiter
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-secondary-700 uppercase tracking-wider">
                            Last Updated
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-secondary-700 uppercase tracking-wider">
                            Status
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-secondary-700 uppercase tracking-wider">
                            Actions
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-surface divide-y divide-secondary-200">
                        {biodataList.map((biodata) => (
                          <tr key={biodata.id} className="hover:bg-secondary-50 transition-colors duration-150">
                            <td className="px-6 py-4 font-mono text-xs">{biodata.worker_id}</td>
                            <td className="px-6 py-4 text-sm">{biodata.full_name || "-"}</td>
                            <td className="px-6 py-4 text-xs">{biodata.passport_no || "-"}</td>
                            <td className="px-6 py-4 text-xs">{biodata.nationality || "-"}</td>
                            <td className="px-6 py-4 text-xs">{biodata.recruiter?.name || "Unknown"}</td>
                            <td className="px-6 py-4 text-xs">
                              {biodata.updated_at ? new Date(biodata.updated_at).toLocaleString() : "-"}
                            </td>
                            <td className="px-6 py-4">
                              <StatusIndicatorBadge status="draft" size="sm" />
                            </td>
                            <td className="px-6 py-4">
                              <button
                                onClick={() => handleViewDetails(biodata)}
                                className="p-2 text-primary hover:bg-primary-50 rounded-md transition-colors duration-150"
                                title="View Draft Details"
                              >
                                <Icon name="Eye" size={16} />
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {biodataPagination && (
                    <div className="px-6 py-4 border-t border-secondary-200">
                      <PaginationComponent
                        pagination={{
                          currentPage: biodataPagination.page || currentPage,
                          perPage: biodataPagination.limit || itemsPerPage,
                          total: biodataPagination.total || 0,
                          totalPages:
                            biodataPagination.totalPages ||
                            biodataPagination.total_pages ||
                            Math.ceil(
                              (biodataPagination.total || 0) /
                                (biodataPagination.limit || itemsPerPage)
                            ),
                          from:
                            ((biodataPagination.page || currentPage) - 1) *
                              (biodataPagination.limit || itemsPerPage) +
                            1,
                          to: Math.min(
                            (biodataPagination.page || currentPage) *
                              (biodataPagination.limit || itemsPerPage),
                            biodataPagination.total || 0
                          ),
                        }}
                        onPageChange={setCurrentPage}
                        onPerPageChange={setItemsPerPage}
                        showPerPageSelector={true}
                      />
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      </main>

      {showDetailsModal && selectedBiodataDetails && (
        <>
          <style>{`
            @media print {
              .biodata-modal-overlay {
                display: none !important;
              }
              .biodata-modal-content {
                position: static !important;
                max-width: 100% !important;
                max-height: none !important;
                box-shadow: none !important;
              }
            }
          `}</style>
          <div className="biodata-modal-overlay fixed inset-0 z-1001 bg-secondary-900 bg-opacity-50 flex items-center justify-center p-4">
            <div className="biodata-modal-content bg-white rounded-lg shadow-xl w-full max-w-5xl max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-secondary-200 flex items-center justify-between">
              <h2 className="text-xl font-bold">Draft Biodata Details</h2>
              <button
                onClick={() => {
                  setShowDetailsModal(false);
                  setSelectedBiodataDetails(null);
                }}
                className="text-secondary-600 hover:text-secondary-900"
              >
                Close
              </button>
            </div>
            <div className="p-6">
              <BiodataDetailsView biodata={selectedBiodataDetails} />
            </div>
          </div>
        </div>
        </>
      )}
    </div>
  );
};

export default ClabBiodataDrafts;
